import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Send, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { waLink, vehicleTexts } from "./vehicleUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full rounded-xl border border-brand/20 bg-white px-4 py-3 text-sm text-forest placeholder:text-brand/40 outline-none transition-all focus:border-gold focus:ring-4 focus:ring-gold/10";
const labelClass = "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-brand";

export const VehicleInterestForm = ({ v, endpoint, title, sub, submitLabel, sentLabel, waText }) => {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const tv = t.vehicles;
  const vt = vehicleTexts(settings, tv);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const wa = waLink(settings, waText || `Bonjour, je suis intéressé par le véhicule ${v.title} (${tv.kind[v.kind]}) vu sur votre site.`);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await axios.post(`${API}${endpoint || `/vehicles/${v.id}/interest`}`, { ...form, lang });
      setSent(r.data);
      toast.success(sentLabel || tv.sent);
    } catch (err) { toast.error(err.response?.data?.detail || tv.sell.error); } finally { setSending(false); }
  };

  return (
    <div className="rounded-2xl border border-brand/10 bg-white p-4 sm:p-5" data-testid="vehicle-interest-block">
      <h4 className="font-display font-bold text-forest">{title || vt.interestTitle}</h4>
      <p className="mt-1 text-xs text-brand-hover">{sub || vt.interestSub}</p>
      {sent ? (
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-mint p-4 text-sm text-forest" data-testid="vehicle-interest-sent"><CheckCircle2 className="h-5 w-5 text-brand" />{sentLabel || tv.sent}</div>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3" data-testid="vehicle-interest-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className={labelClass}>{t.contact.form.name} *</label><input required value={form.name} onChange={set("name")} data-testid="vehicle-interest-name" className={inputClass} placeholder={t.contact.form.namePh} /></div>
            <div><label className={labelClass}>{t.contact.form.phoneSimple} *</label><input required value={form.phone} onChange={set("phone")} data-testid="vehicle-interest-phone" className={inputClass} placeholder={t.contact.form.phonePh} /></div>
          </div>
          <div><label className={labelClass}>{t.contact.form.emailSimple}</label><input type="email" value={form.email} onChange={set("email")} data-testid="vehicle-interest-email" className={inputClass} placeholder={t.contact.form.emailPh} /></div>
          <div><label className={labelClass}>{tv.message}</label><textarea rows={2} value={form.message} onChange={set("message")} data-testid="vehicle-interest-message" className={`${inputClass} resize-none`} placeholder={tv.messagePh} /></div>
          <button type="submit" disabled={sending} data-testid="vehicle-interest-submit" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-gold/25 transition-all hover:bg-gold-hover disabled:opacity-60"><Send className="h-4 w-4" />{sending ? tv.sending : (submitLabel || tv.send)}</button>
        </form>
      )}
      {(sent?.whatsapp_url || wa) && <a href={sent?.whatsapp_url || wa} target="_blank" rel="noreferrer" data-testid="vehicle-interest-whatsapp" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-brand px-6 py-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand transition-all hover:bg-brand hover:text-white"><MessageCircle className="h-4 w-4" />{tv.whatsapp}</a>}
    </div>
  );
};
